import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  List, 
  Calendar, 
  Package, 
  Eye, 
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  Send,
  Plus
} from 'lucide-react';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';

function Ordini() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ordini, setOrdini] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrdine, setSelectedOrdine] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [showWhatsAppLinksModal, setShowWhatsAppLinksModal] = useState(false);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [ordineIdWhatsApp, setOrdineIdWhatsApp] = useState(null);

  useEffect(() => {
    loadOrdini();
    loadFornitori();
  }, []);

  const loadOrdini = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/ordini', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setOrdini(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const loadFornitori = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/fornitori', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setFornitori(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
    }
  };

  const viewOrdineDetails = async (ordineId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/ordini/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSelectedOrdine(response.data.data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Errore caricamento dettagli ordine:', error);
      toast.error('Errore nel caricamento dei dettagli');
    }
  };

  const deleteOrdine = async (ordineId) => {
    setConfirmData({ ordineId, action: 'delete' });
    setConfirmAction(() => () => executeDeleteOrdine(ordineId));
    setShowConfirmModal(true);
  };

  const executeDeleteOrdine = async (ordineId) => {
    setShowConfirmModal(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/ordini/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Ordine eliminato con successo');
        loadOrdini();
        if (showDetails) {
          setShowDetails(false);
          setSelectedOrdine(null);
        }
      }
    } catch (error) {
      console.error('Errore eliminazione ordine:', error);
      toast.error('Errore nell\'eliminazione dell\'ordine');
    }
  };

  const riapriOrdine = async (ordineId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/ordini/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Salva l'ordine nel localStorage per riaprirlo nella pagina NuovoOrdine
        localStorage.setItem('ordineDaRiaprire', JSON.stringify(response.data.data));
        toast.success('Ordine caricato per la modifica');
        // Naviga alla pagina NuovoOrdine
        window.location.href = '/nuovo-ordine';
      }
    } catch (error) {
      console.error('Errore caricamento ordine:', error);
      toast.error('Errore nel caricamento dell\'ordine');
    }
  };

  const creaOrdineAggiuntivo = async (ordineId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/ordini/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Naviga alla pagina OrdineAggiuntivo passando l'ordine originale
        navigate('/ordine-aggiuntivo', { 
          state: { ordineOriginale: response.data.data } 
        });
      }
    } catch (error) {
      console.error('Errore caricamento ordine:', error);
      toast.error('Errore nel caricamento dell\'ordine');
    }
  };

  const handleInviaWhatsAppSingolo = async (linkData) => {
    const token = localStorage.getItem('token');
    
    try {
      // Apri il link WhatsApp
      window.open(linkData.link, '_blank');
      
      // Marca come inviato nel database
      await axios.put(`/api/whatsapp/marca-inviato/${linkData.messaggio_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Aggiorna lo stato locale
      const updated = whatsappLinks.map(link =>
        link.messaggio_id === linkData.messaggio_id ? { ...link, inviato: true } : link
      );
      setWhatsappLinks(updated);

      // Se con questo invio tutti i messaggi sono stati inviati, passa l'ordine a "inviato"
      if (updated.every(link => link.inviato) && ordineIdWhatsApp) {
        try {
          await axios.put(`/api/ordini/${ordineIdWhatsApp}/stato`, { stato: 'sent' }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Tutti i messaggi inviati. Ordine segnato come Inviato.');
          loadOrdini();
        } catch (err) {
          console.error('Errore aggiornamento stato ordine:', err);
        }
      }
      
      toast.success(`Messaggio aperto per ${linkData.fornitore}`, { duration: 2000 });
    } catch (error) {
      console.error('Errore registrazione invio:', error);
      toast.error('Errore nella registrazione dell\'invio');
    }
  };

  const handleChiudiModalWhatsApp = async () => {
    // Controlla se ci sono messaggi non inviati
    const nonInviati = whatsappLinks.filter(link => !link.inviato);
    
    if (nonInviati.length > 0) {
      const conferma = window.confirm(
        `Attenzione! Hai ${nonInviati.length} messaggi non ancora inviati:\n\n` +
        nonInviati.map(link => `- ${link.fornitore}`).join('\n') +
        `\n\nSei sicuro di voler chiudere? Potrai sempre completare l'invio successivamente.`
      );
      
      if (!conferma) return;
    }
    
    // Verifica se tutti i messaggi sono stati inviati
    const tuttiInviati = whatsappLinks.every(link => link.inviato);
    
    if (tuttiInviati && ordineIdWhatsApp) {
      // Aggiorna lo stato dell'ordine a 'sent'
      const token = localStorage.getItem('token');
      try {
        await axios.put(`/api/ordini/${ordineIdWhatsApp}/stato`, {
          stato: 'sent'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Stato ordine aggiornato a "sent"');
        toast.success('Ordine completato e inviato!');
        loadOrdini(); // Ricarica la lista
      } catch (error) {
        console.error('❌ Errore aggiornamento stato ordine:', error);
      }
    }
    
    setShowWhatsAppLinksModal(false);
    setWhatsappLinks([]);
    setOrdineIdWhatsApp(null);
  };

  const rigeneraMessaggiWhatsApp = async () => {
    if (!ordineIdWhatsApp) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Carica i dettagli dell'ordine per rigenerare i messaggi
      const ordineResponse = await axios.get(`/api/ordini/${ordineIdWhatsApp}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!ordineResponse.data.success) {
        throw new Error('Errore nel recupero dell\'ordine');
      }

      const ordine = ordineResponse.data.data;
      
      // Raggruppa prodotti per fornitore
      const groupedByFornitore = {};
      ordine.prodotti.forEach(prodotto => {
        if (prodotto.fornitore_id) {
          if (!groupedByFornitore[prodotto.fornitore_id]) {
            groupedByFornitore[prodotto.fornitore_id] = {
              fornitore_id: prodotto.fornitore_id,
              fornitore_nome: prodotto.fornitore_nome || 'Fornitore sconosciuto',
              prodotti: []
            };
          }
          groupedByFornitore[prodotto.fornitore_id].prodotti.push(prodotto);
        }
      });

      // Rigenera link per ogni fornitore (il backend eliminerà automaticamente i pending esistenti)
      const linkWhatsApp = [];
      
      for (const gruppo of Object.values(groupedByFornitore)) {
        try {
          const notaFornitore = ordine.note_personalizzate?.[`fornitore_${gruppo.fornitore_id}`] || '';
          
          // intro/outro vengono presi automaticamente dal database del fornitore
          const messaggioResponse = await axios.post('/api/whatsapp/genera-messaggio', {
            ordine_id: ordineIdWhatsApp,
            fornitore_id: gruppo.fornitore_id,
            nota_personalizzata: notaFornitore
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (messaggioResponse.data.success) {
            const messaggioData = messaggioResponse.data.data;
            linkWhatsApp.push({
              messaggio_id: messaggioData.messaggio_id,
              fornitore_id: gruppo.fornitore_id,
              fornitore: gruppo.fornitore_nome,
              link: messaggioData.whatsappLink,
              messaggio: messaggioData.messaggio,
              inviato: false
            });
          }
        } catch (error) {
          console.error(`Errore rigenerazione link per fornitore ${gruppo.fornitore_nome}:`, error);
        }
      }

      if (linkWhatsApp.length > 0) {
        setWhatsappLinks(linkWhatsApp);
        toast.success(`${linkWhatsApp.length} messaggi rigenerati con successo!`, { duration: 3000 });
      } else {
        toast.error('Nessun messaggio rigenerato');
      }
    } catch (error) {
      console.error('Errore rigenerazione messaggi:', error);
      toast.error('Errore nella rigenerazione dei messaggi WhatsApp');
    }
  };

  const inviaWhatsAppOrdine = async (ordineId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Carica invii già esistenti dal database
      const inviEsistentiResponse = await axios.get(`/api/whatsapp/invii-ordine/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const inviEsistenti = inviEsistentiResponse.data.data || [];
      
      // Ottieni dettagli ordine
      const ordineResponse = await axios.get(`/api/ordini/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!ordineResponse.data.success) {
        throw new Error('Errore nel recupero dell\'ordine');
      }

      const ordine = ordineResponse.data.data;
      
      // Raggruppa prodotti per fornitore
      const groupedByFornitore = {};
      ordine.prodotti.forEach(prodotto => {
        if (prodotto.fornitore_id) {
          if (!groupedByFornitore[prodotto.fornitore_id]) {
            groupedByFornitore[prodotto.fornitore_id] = {
              fornitore_id: prodotto.fornitore_id,
              fornitore_nome: prodotto.fornitore_nome || 'Fornitore sconosciuto',
              prodotti: []
            };
          }
          groupedByFornitore[prodotto.fornitore_id].prodotti.push(prodotto);
        }
      });

      // Genera o recupera link per ogni fornitore
      const linkWhatsApp = [];
      
      for (const gruppo of Object.values(groupedByFornitore)) {
        try {
          // Controlla se esiste già un invio per questo fornitore
          const invioEsistente = inviEsistenti.find(inv => inv.fornitore_id === gruppo.fornitore_id);
          
          // Se esiste un messaggio già inviato, lo mostriamo ma permetteremo rigenerazione
          if (invioEsistente && invioEsistente.inviato === 1) {
            // Usa l'invio esistente (già inviato)
            linkWhatsApp.push({
              messaggio_id: invioEsistente.id,
              fornitore_id: gruppo.fornitore_id,
              fornitore: gruppo.fornitore_nome,
              link: invioEsistente.whatsapp_link,
              messaggio: invioEsistente.messaggio,
              inviato: true
            });
          } else {
            // Genera nuovo messaggio o usa quello pending esistente
            const notaFornitore = ordine.note_personalizzate?.[`fornitore_${gruppo.fornitore_id}`] || '';
            
            // intro/outro vengono presi automaticamente dal database del fornitore
            const messaggioResponse = await axios.post('/api/whatsapp/genera-messaggio', {
              ordine_id: ordineId,
              fornitore_id: gruppo.fornitore_id,
              nota_personalizzata: notaFornitore
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (messaggioResponse.data.success) {
              const messaggioData = messaggioResponse.data.data;
              linkWhatsApp.push({
                messaggio_id: messaggioData.messaggio_id,
                fornitore_id: gruppo.fornitore_id,
                fornitore: gruppo.fornitore_nome,
                link: messaggioData.whatsappLink,
                messaggio: messaggioData.messaggio,
                inviato: invioEsistente ? (invioEsistente.inviato === 1) : false
              });
            }
          }
        } catch (error) {
          console.error(`Errore generazione link per fornitore ${gruppo.fornitore_nome}:`, error);
        }
      }

      if (linkWhatsApp.length > 0) {
        // Mostra il modal con i pulsanti
        setWhatsappLinks(linkWhatsApp);
        setOrdineIdWhatsApp(ordineId);
        setShowWhatsAppLinksModal(true);
        
        const giàInviati = linkWhatsApp.filter(l => l.inviato).length;
        if (giàInviati > 0) {
          toast(`Hai già inviato ${giàInviati}/${linkWhatsApp.length} messaggi`, { 
            icon: 'ℹ️',
            duration: 3000 
          });
        } else {
          toast.success(`${linkWhatsApp.length} messaggi pronti da inviare!`, { duration: 3000 });
        }
      } else {
        toast.error('Nessun messaggio da inviare');
      }
    } catch (error) {
      console.error('Errore invio WhatsApp:', error);
      toast.error('Errore nella preparazione dei messaggi WhatsApp');
    }
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'draft':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatoText = (stato) => {
    switch (stato) {
      case 'draft':
        return 'Bozza';
      case 'sent':
        return 'Inviato';
      case 'completed':
        return 'Completato';
      default:
        return 'Errore';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Ordini</h1>
        <p className="mt-1 text-sm text-gray-500">
          Gestisci tutti gli ordini fornitori
        </p>
      </div>

      {/* Orders List */}
      <div className="bg-white shadow rounded-lg">
        {ordini.length === 0 ? (
          <div className="text-center py-12">
            <List className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun ordine trovato</h3>
            <p className="mt-1 text-sm text-gray-500">
              Crea il tuo primo ordine per iniziare.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {ordini.map((ordine) => (
              <div key={ordine.id} className="p-6 hover:bg-gray-50 overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center flex-wrap gap-2">
                      {getStatoIcon(ordine.stato)}
                      <h3 className="text-lg font-medium text-gray-900">
                        Ordine #{ordine.id}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                        ordine.stato === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        ordine.stato === 'sent' ? 'bg-green-100 text-green-800' :
                        ordine.stato === 'completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {getStatoText(ordine.stato)}
                      </span>
                    </div>
                    
                    <div className="mt-2 flex items-center text-sm text-gray-500 flex-wrap gap-x-4">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                        {formatDate(ordine.data_ordine)}
                      </div>
                      <div className="flex items-center">
                        <Package className="h-4 w-4 mr-1 flex-shrink-0" />
                        {ordine.numero_prodotti} prodotti
                      </div>
                    </div>
                    
                    {ordine.prodotti_lista && (
                      <p className="mt-2 text-sm text-gray-600 break-words line-clamp-2">
                        {ordine.prodotti_lista}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => viewOrdineDetails(ordine.id)}
                      className="btn btn-outline btn-sm flex-shrink-0 flex items-center justify-center min-w-[44px]"
                    >
                      <Eye className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Dettagli</span>
                    </button>
                    {ordine.stato === 'draft' && (
                      <>
                        <button
                          onClick={() => riapriOrdine(ordine.id)}
                          className="btn btn-outline btn-sm text-blue-600 hover:bg-blue-50 flex-shrink-0 flex items-center justify-center min-w-[44px]"
                        >
                          <Edit className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Riapri</span>
                        </button>
                        {user?.ruolo === 'admin' && (
                          <button
                            onClick={() => inviaWhatsAppOrdine(ordine.id)}
                            className="btn btn-outline btn-sm text-green-600 hover:bg-green-50 flex-shrink-0 flex items-center justify-center min-w-[44px]"
                          >
                            <Send className="h-4 w-4 sm:mr-1" />
                            <span className="hidden sm:inline">Invia WhatsApp</span>
                          </button>
                        )}
                      </>
                    )}
                    {(ordine.stato === 'sent' || ordine.stato === 'completed') && (
                      <button
                        onClick={() => creaOrdineAggiuntivo(ordine.id)}
                        className="btn btn-outline btn-sm text-orange-600 hover:bg-orange-50 flex-shrink-0 flex items-center justify-center min-w-[44px]"
                      >
                        <Plus className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">Ordine Aggiuntivo</span>
                      </button>
                    )}
                    {/* Mostra elimina solo se admin o se operatore ha creato lui l'ordine */}
                    {(user?.ruolo === 'admin' || ordine.created_by === user?.id) && (
                      <button
                        onClick={() => deleteOrdine(ordine.id)}
                        className="btn btn-outline btn-sm text-red-600 hover:bg-red-50 flex-shrink-0 flex items-center justify-center min-w-[44px]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showDetails && selectedOrdine && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetails(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Dettagli Ordine #{selectedOrdine.id}
                  </h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 mr-2" />
                    Data: {formatDate(selectedOrdine.data_ordine)}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    {getStatoIcon(selectedOrdine.stato)}
                    <span className="ml-2">Stato: {getStatoText(selectedOrdine.stato)}</span>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Prodotti per Fornitore:</h4>
                    <div className="space-y-4">
                      {(() => {
                        // Raggruppa prodotti per fornitore
                        const grouped = {};
                        selectedOrdine.prodotti?.forEach(prodotto => {
                          const fornitoreId = prodotto.fornitore_id || 'senza_fornitore';
                          if (!grouped[fornitoreId]) {
                            grouped[fornitoreId] = {
                              fornitore_id: prodotto.fornitore_id,
                              fornitore: fornitori.find(f => f.id === prodotto.fornitore_id),
                              prodotti: []
                            };
                          }
                          grouped[fornitoreId].prodotti.push(prodotto);
                        });
                        
                        // Ordina prodotti alfabeticamente per ogni fornitore
                        Object.keys(grouped).forEach(fornitoreId => {
                          grouped[fornitoreId].prodotti.sort((a, b) => a.nome.localeCompare(b.nome));
                        });
                        
                        return Object.entries(grouped).map(([fornitoreId, gruppo]) => {
                          if (!gruppo.fornitore) return null;
                          
                          // Ottieni la nota personalizzata per questo fornitore
                          const notaKey = `fornitore_${fornitoreId}`;
                          const notaFornitore = selectedOrdine.note_personalizzate?.[notaKey] || '';
                          
                          return (
                            <div key={fornitoreId} className="border border-gray-200 rounded-lg overflow-hidden">
                              {/* Header Fornitore */}
                              <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200">
                                <div className="flex items-center">
                                  <Package className="h-5 w-5 mr-2 text-blue-600" />
                                  <h5 className="text-sm font-semibold text-blue-900">
                                    {gruppo.fornitore.nome}
                                  </h5>
                                  <span className="ml-auto text-xs text-blue-600 font-medium">
                                    {gruppo.prodotti.length} prodott{gruppo.prodotti.length === 1 ? 'o' : 'i'}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Lista Prodotti */}
                              <div className="p-3 bg-white space-y-2">
                                {gruppo.prodotti.map((prodotto) => (
                                  <div key={prodotto.id} className="bg-gray-50 rounded p-3">
                                    <p className="text-sm font-medium text-gray-900">
                                      {prodotto.quantita} {prodotto.tipologia_scelta ? `${prodotto.tipologia_scelta} ` : ''}{prodotto.nome}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">{prodotto.categoria}</p>
                                    {prodotto.note && (
                                      <div className="mt-2 p-2 bg-yellow-50 rounded border-l-2 border-yellow-400">
                                        <p className="text-xs text-yellow-800">
                                          <span className="font-medium">📌 Nota prodotto:</span> {prodotto.note}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                
                                {/* Nota Personalizzata Fornitore */}
                                {notaFornitore && (
                                  <div className="mt-3 p-3 bg-blue-50 rounded border-l-3 border-blue-400">
                                    <p className="text-xs font-medium text-blue-800 mb-1">
                                      📝 Note aggiuntive per {gruppo.fornitore.nome}:
                                    </p>
                                    <p className="text-sm text-blue-700">{notaFornitore}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowDetails(false)}
                  className="btn btn-primary"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale di conferma eliminazione */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare l'ordine #${confirmData?.ordineId}?\n\nQuesta azione non può essere annullata.`}
        type="warning"
        confirmText="Sì, Elimina"
        cancelText="Annulla"
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
      />

      {/* Modale invio WhatsApp con pulsanti manuali */}
      {showWhatsAppLinksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">
                📱 Invia Messaggi WhatsApp
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Clicca su ogni fornitore per inviare il messaggio. I messaggi si apriranno uno alla volta.
              </p>
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-medium">
                  {whatsappLinks.filter(l => l.inviato).length} / {whatsappLinks.length} inviati
                </span>
              </div>
            </div>

            <div className="px-6 py-4 space-y-3">
              {whatsappLinks.map((linkData, index) => (
                <div 
                  key={index}
                  className={`border rounded-lg p-4 transition-all ${
                    linkData.inviato 
                      ? 'bg-green-50 border-green-300' 
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-medium text-gray-900">
                          {linkData.fornitore}
                        </span>
                        {linkData.inviato && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓ Inviato
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                        {linkData.messaggio.substring(0, 100)}...
                      </p>
                    </div>
                    <button
                      onClick={() => handleInviaWhatsAppSingolo(linkData)}
                      disabled={linkData.inviato}
                      className={`ml-4 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        linkData.inviato
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {linkData.inviato ? '✓ Inviato' : '📱 Invia'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              {whatsappLinks.filter(l => !l.inviato).length > 0 && (
                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Attenzione: {whatsappLinks.filter(l => !l.inviato).length} messaggi non ancora inviati
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                {whatsappLinks.some(l => l.inviato) && (
                  <button
                    onClick={rigeneraMessaggiWhatsApp}
                    className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md text-sm font-medium"
                  >
                    🔄 Re-invia (rigenera tutti)
                  </button>
                )}
                <button
                  onClick={handleChiudiModalWhatsApp}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                >
                  {whatsappLinks.every(l => l.inviato) ? 'Fatto, Chiudi' : 'Chiudi (puoi completare dopo)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Ordini;

